import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge } from '@/components/ui/badge';
import { getPredictionStrategyColors } from '@/types/rebateAnalytics';
import { colors, ForecastLegend } from '@/components/rebates/ForecastCard';
import {
  nextForecastLegendVisibility,
  type ForecastLegendEntry,
} from '@/components/rebates/ForecastLegend';
import {
  compactPurchaseTrendCurrency,
  defaultPurchaseTrendVisibleLines,
  purchaseTrendPurchasesLegendIsSplit,
  purchaseTrendPurchasesSeriesLabel,
  purchaseTrendReportedLegendIsSplit,
  purchaseTrendVisibleSeries,
  PURCHASE_TREND_LEGEND,
  type PurchaseTrendChartRow,
} from './purchaseTrendChart';
import { PurchaseTrendTooltip } from './PurchaseTrendTooltip';

const PURCHASES_ACTUAL_COLOR = '#2563eb';
const PURCHASES_FORECAST_COLOR = '#93c5fd';
const PRIOR_YEAR_COLOR = '#9ca3af';
export const SUPPLIER_PURCHASES_COLOR = 'var(--color-chart-2)';

export function reportedLineColors(reportedAlgorithm: string | null): {
  past: string;
  future: string;
} {
  return {
    past: colors.black.main,
    future: getPredictionStrategyColors(
      reportedAlgorithm ?? 'linear-regression',
    ).future,
  };
}

function PurchaseTrendEmptyState() {
  return (
    <p className="py-12 text-center text-sm text-muted-foreground">
      <T context="empty state when the purchases trend chart has no months">
        No purchase trend data for this range.
      </T>
    </p>
  );
}

function PurchaseTrendSeriesAnnouncements({
  showPurchases,
  showPriorYear,
  showReported,
  showSupplierPurchases,
  purchasesLabel,
  priorYearLabel,
  reportedLabel,
  supplierPurchasesLabel,
  rows,
}: {
  showPurchases: boolean;
  showPriorYear: boolean;
  showReported: boolean;
  showSupplierPurchases: boolean;
  purchasesLabel: string;
  priorYearLabel: string;
  reportedLabel: string;
  supplierPurchasesLabel: string;
  rows: PurchaseTrendChartRow[];
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex flex-wrap gap-2" data-testid="purchase-trend-series">
        {showPurchases ? (
          <span data-testid="series-purchases" className="sr-only">
            {purchasesLabel}
          </span>
        ) : null}
        {showPriorYear ? (
          <span data-testid="series-prior-year" className="sr-only">
            {priorYearLabel}
          </span>
        ) : null}
        {showReported ? (
          <span data-testid="series-rebate-earnings" className="sr-only">
            {reportedLabel}
          </span>
        ) : null}
        {showSupplierPurchases ? (
          <span data-testid="series-reported-volume" className="sr-only">
            {supplierPurchasesLabel}
          </span>
        ) : null}
        {rows
          .filter((row) => row.isPartialMonth)
          .map((row) => (
            <Badge
              key={row.month}
              variant="secondary"
              data-testid="partial-month-flag"
            >
              <T context="badge marking the purchases file through-month as partial">
                <Var>{row.monthLabel}</Var> is a partial through-month
              </T>
            </Badge>
          ))}
      </div>
    </div>
  );
}

function purchaseTrendLegendEntries(input: {
  rows: PurchaseTrendChartRow[];
  reportedAlgorithm: string | null;
  purchasesLabel: string;
  priorYearLabel: string;
  reportedLabel: string;
  supplierPurchasesLabel: string;
  reportedTooltip: string;
  priorYearTooltip: string;
  purchasesTooltip: string;
  supplierPurchasesTooltip: string;
}): ForecastLegendEntry[] {
  const lineColors = reportedLineColors(input.reportedAlgorithm);
  return [
    {
      label: input.reportedLabel,
      colors: purchaseTrendReportedLegendIsSplit(input.rows)
        ? [lineColors.past, lineColors.future]
        : [lineColors.past],
      isSplit: purchaseTrendReportedLegendIsSplit(input.rows),
      lineType: PURCHASE_TREND_LEGEND.reported,
      tooltip: input.reportedTooltip,
    },
    {
      label: input.priorYearLabel,
      colors: [PRIOR_YEAR_COLOR],
      isSplit: false,
      lineType: PURCHASE_TREND_LEGEND.priorYear,
      tooltip: input.priorYearTooltip,
    },
    {
      label: input.purchasesLabel,
      colors: purchaseTrendPurchasesLegendIsSplit(input.rows)
        ? [PURCHASES_ACTUAL_COLOR, PURCHASES_FORECAST_COLOR]
        : [PURCHASES_ACTUAL_COLOR],
      isSplit: purchaseTrendPurchasesLegendIsSplit(input.rows),
      lineType: PURCHASE_TREND_LEGEND.purchases,
      tooltip: input.purchasesTooltip,
      testId: 'purchase-trend-legend-purchases',
      swatchTestId: 'purchase-trend-legend-purchases-swatch',
    },
    {
      label: input.supplierPurchasesLabel,
      colors: [SUPPLIER_PURCHASES_COLOR],
      isSplit: false,
      lineType: PURCHASE_TREND_LEGEND.supplierPurchases,
      tooltip: input.supplierPurchasesTooltip,
      testId: 'purchase-trend-legend-supplier-purchases',
      swatchTestId: 'purchase-trend-legend-supplier-purchases-swatch',
    },
  ];
}

export function PurchasesVsRebatesChart({
  rows,
  reportedAlgorithm,
  visibleLines: visibleLinesProp,
  onVisibleLinesChange,
}: {
  rows: PurchaseTrendChartRow[];
  reportedAlgorithm: string | null;
  visibleLines?: Set<string>;
  onVisibleLinesChange?: (lines: Set<string>) => void;
}) {
  const gt = useGT();
  const [uncontrolledVisibleLines, setUncontrolledVisibleLines] = useState(
    defaultPurchaseTrendVisibleLines,
  );
  const visibleLines = visibleLinesProp ?? uncontrolledVisibleLines;
  function setVisibleLines(next: Set<string>) {
    if (onVisibleLinesChange) {
      onVisibleLinesChange(next);
      return;
    }
    setUncontrolledVisibleLines(next);
  }
  const purchasesSplit = purchaseTrendPurchasesLegendIsSplit(rows);
  const purchasesLabel = purchaseTrendPurchasesSeriesLabel(gt, purchasesSplit);
  const priorYearLabel = gt('Prior-year purchases', {
    context: 'purchases vs rebates chart series',
  });
  const supplierPurchasesLabel = gt('Supplier purchases', {
    context: 'purchases vs rebates chart series',
  });
  const reportedLabel = gt('Reported Rebates', {
    context: 'Purchases vs Rebates chart series label',
  });
  const lineColors = reportedLineColors(reportedAlgorithm);
  const reportedFutureName = gt('Reported Rebates (Future)', {
    context: 'Purchases vs Rebates chart series label',
  });
  const {
    purchases: showPurchases,
    priorYear: showPriorYear,
    reported: showReported,
    supplierPurchases: showSupplierPurchases,
  } = purchaseTrendVisibleSeries(visibleLines);

  if (rows.length === 0) {
    return <PurchaseTrendEmptyState />;
  }

  return (
    <div className="space-y-3">
      <PurchaseTrendSeriesAnnouncements
        showPurchases={showPurchases}
        showPriorYear={showPriorYear}
        showReported={showReported}
        showSupplierPurchases={showSupplierPurchases}
        purchasesLabel={purchasesLabel}
        priorYearLabel={priorYearLabel}
        reportedLabel={reportedLabel}
        supplierPurchasesLabel={supplierPurchasesLabel}
        rows={rows}
      />
      <div className="h-80 w-full" data-testid="purchases-vs-rebates-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={rows}
            margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
            barCategoryGap="18%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              className="stroke-border"
            />
            <XAxis dataKey="monthLabel" tick={{ fontSize: 12 }} />
            <YAxis
              yAxisId="purchases"
              tickFormatter={compactPurchaseTrendCurrency}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              yAxisId="earnings"
              orientation="right"
              tickFormatter={compactPurchaseTrendCurrency}
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<PurchaseTrendTooltip />} />
            {showPurchases ? (
              <Bar
                yAxisId="purchases"
                dataKey="purchases"
                name={purchasesLabel}
                fill={PURCHASES_ACTUAL_COLOR}
                fillOpacity={0.9}
                isAnimationActive={false}
              >
                {rows.map((row) => (
                  <Cell
                    key={row.month}
                    fill={
                      row.purchasesKind === 'forecast'
                        ? PURCHASES_FORECAST_COLOR
                        : PURCHASES_ACTUAL_COLOR
                    }
                  />
                ))}
              </Bar>
            ) : null}
            {showPriorYear ? (
              <Line
                yAxisId="purchases"
                type="monotone"
                dataKey="priorYearPurchases"
                name={priorYearLabel}
                stroke={PRIOR_YEAR_COLOR}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={{ r: 3 }}
                isAnimationActive={false}
              />
            ) : null}
            {showSupplierPurchases ? (
              <Line
                yAxisId="purchases"
                type="monotone"
                dataKey="reportedVolume"
                name={supplierPurchasesLabel}
                stroke={SUPPLIER_PURCHASES_COLOR}
                strokeWidth={2}
                dot={{ r: 3 }}
                isAnimationActive={false}
              />
            ) : null}
            {showReported ? (
              <Line
                yAxisId="earnings"
                type="monotone"
                dataKey="reported_future"
                name={reportedFutureName}
                stroke={lineColors.future}
                strokeWidth={4}
                dot={{ fill: lineColors.future, strokeWidth: 2, r: 6 }}
                activeDot={{
                  r: 8,
                  stroke: lineColors.future,
                  strokeWidth: 2,
                }}
                connectNulls={false}
                legendType="none"
                isAnimationActive={false}
              />
            ) : null}
            {showReported ? (
              <Line
                yAxisId="earnings"
                type="monotone"
                dataKey="reported"
                name={reportedLabel}
                stroke={lineColors.past}
                strokeWidth={4}
                dot={{ fill: lineColors.past, strokeWidth: 2, r: 6 }}
                activeDot={{
                  r: 8,
                  stroke: lineColors.past,
                  strokeWidth: 2,
                }}
                connectNulls={false}
                legendType="none"
                isAnimationActive={false}
              />
            ) : null}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <ForecastLegend
        entries={purchaseTrendLegendEntries({
          rows,
          reportedAlgorithm,
          purchasesLabel,
          priorYearLabel,
          reportedLabel,
          supplierPurchasesLabel,
          reportedTooltip: gt(
            'Rebates earned, based on amounts suppliers have reported.',
            {
              context: 'purchases vs rebates reported legend tooltip',
            },
          ),
          priorYearTooltip: gt(
            'Purchases from the prior year for the same months',
            {
              context: 'purchases vs rebates prior-year legend tooltip',
            },
          ),
          purchasesTooltip: gt(
            'Uploaded purchases, including forecast months after the file through-month',
            {
              context: 'purchases vs rebates purchases legend tooltip',
            },
          ),
          supplierPurchasesTooltip: gt(
            'Show purchases as reported by supplier',
            {
              context: 'purchases vs rebates supplier purchases legend tooltip',
            },
          ),
        })}
        visibleLines={visibleLines}
        onToggle={(lineType) => {
          const next = nextForecastLegendVisibility(visibleLines, lineType);
          if (next) {
            setVisibleLines(next);
          }
        }}
        canToggle
        testId="purchase-trend-legend"
      />
    </div>
  );
}
